import { useState } from "react";
import { UserPlus, Trash2, Users, UserX, UserCheck, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, SectionTitle, Field, FormError, SubmitButton, LoadingState, ErrorState, StatusBadge, ConfirmDialog, EmptyState } from "@/components/common";
import { useGarage } from "@/context/GarageContext";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/lib/useFetch";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { t } from "@/i18n";
import { TempMemberButton } from "@/components/TempMemberButton";

function InviteForm({ onDone, disabled }) {
  const { me } = useAuth();
  const [f, setF] = useState({ name: "", email: "", role: "mechanic" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/garage/employees/invite", f);
      toast.success(t(me.demo_mode ? "invite.createdDemo" : "invite.createdNoProvider"));
      setF({ name: "", email: "", role: "mechanic" });
      onDone();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="invite-employee-form">
      <FormError message={error} />
      <div className="grid gap-4 sm:grid-cols-3">
        <Field label={t("invite.fullName")} htmlFor="ie-name"><Input id="ie-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="invite-employee-name-input" /></Field>
        <Field label={t("auth.email")} htmlFor="ie-email"><Input id="ie-email" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="invite-employee-email-input" /></Field>
        <Field label={t("employees.role")}>
          <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
            <SelectTrigger data-testid="invite-employee-role-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="mechanic">{t("status.mechanic")}</SelectItem>
              <SelectItem value="garage_manager">{t("status.garage_manager")}</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      </div>
      <SubmitButton busy={busy} disabled={disabled} data-testid="invite-employee-submit-button"><UserPlus className="mr-2 h-4 w-4" /> {t("employees.invite")}</SubmitButton>
    </form>
  );
}

export function EmployeesManager() {
  const { canManage, garage } = useGarage();
  const { data, error, loading, reload } = useFetch("/garage/employees");
  const plan = useFetch("/garage/plan");
  const [removing, setRemoving] = useState(null);
  const [deactivating, setDeactivating] = useState(null);

  const setStatus = async (m, status) => {
    try {
      await api.put(`/garage/employees/${m.membership_id}/status`, { status });
      toast.success(t(status === "active" ? "p7.access.reactivated" : "p7.access.deactivated"));
      setDeactivating(null);
      reload();
      plan.reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const resend = async (inv) => {
    try {
      await api.post(`/garage/invites/${inv.id}/resend`);
      toast.success(t("p7.access.resent"));
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const changeRole = async (m, role) => {
    try {
      await api.put(`/garage/employees/${m.membership_id}`, { role });
      toast.success(t("common.saved"));
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const remove = async () => {
    try {
      await api.delete(`/garage/employees/${removing.membership_id}`);
      toast.success(t("employees.removed"));
      setRemoving(null);
      reload();
      plan.reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const revoke = async (inv) => {
    try {
      await api.delete(`/garage/invites/${inv.id}`);
      toast.success(t("employees.revoked"));
      reload();
      plan.reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (loading && !data) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  const max = plan.data?.plan?.max_internal_users;
  return (
    <div className="space-y-6" data-testid="employees-manager">
      <Card testId="seat-usage-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">{t("employees.seats")}</p>
            <p className="mt-1 font-display text-3xl font-extrabold text-slate-900" data-testid="seat-usage-value">{data.seats_used} / {max ?? "—"}</p>
          </div>
          <p className="max-w-md text-sm text-slate-600">{t("employees.seatsHint", { plan: plan.data?.plan?.name || "—" })}</p>
        </div>
      </Card>
      <Card>
        <SectionTitle>{t("employees.team")}</SectionTitle>
        <div className="space-y-2" data-testid="employees-list">
          {data.members.map((m) => (
            <div key={m.membership_id} className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 p-3" data-testid={`employee-row-${m.email}`}>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-slate-900">{m.name} {m.is_self && <span className="text-xs font-normal text-slate-500">({t("employees.you")})</span>}</p>
                <p className="truncate text-sm text-slate-500">{m.email}</p>
                {m.status === "inactive" && <p className="text-xs font-semibold text-slate-500" data-testid={`employee-inactive-${m.email}`}>{t("p7.access.inactive")}</p>}
              </div>
              {canManage && !m.is_self && m.status === "active" ? (
                <Select value={m.role} onValueChange={(v) => changeRole(m, v)}>
                  <SelectTrigger className="w-44" data-testid={`employee-role-select-${m.email}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mechanic">{t("status.mechanic")}</SelectItem>
                    <SelectItem value="garage_manager">{t("status.garage_manager")}</SelectItem>
                  </SelectContent>
                </Select>
              ) : <StatusBadge status={m.role} />}
              {canManage && !m.is_self && (m.status === "inactive" ? (
                <Button variant="outline" size="sm" onClick={() => setStatus(m, "active")} data-testid={`reactivate-employee-${m.email}`}><UserCheck className="mr-1.5 h-4 w-4" />{t("p7.access.reactivate")}</Button>
              ) : (
                <Button variant="outline" size="sm" onClick={() => setDeactivating(m)} data-testid={`deactivate-employee-${m.email}`}><UserX className="mr-1.5 h-4 w-4" />{t("p7.access.deactivate")}</Button>
              ))}
              {canManage && !m.is_self && (
                <Button variant="ghost" size="icon" className="text-red-600" onClick={() => setRemoving(m)} data-testid={`remove-employee-${m.email}`} aria-label={t("common.remove")}><Trash2 className="h-4 w-4" /></Button>
              )}
            </div>
          ))}
        </div>
        <h3 className="mb-3 mt-8 text-sm font-semibold text-slate-800">{t("employees.pending")}</h3>
        {data.invites.length === 0 ? <p className="text-sm text-slate-500" data-testid="no-pending-invites">{t("employees.noPending")}</p> : (
          <div className="space-y-2" data-testid="pending-invites-list">
            {data.invites.map((i) => (
              <div key={i.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-dashed border-slate-300 p-3 text-sm">
                <span className="min-w-0 flex-1 truncate">{i.email}</span>
                <StatusBadge status={i.role} />
                <span className="text-xs text-slate-500">{t("employees.expires", { date: fmtDate(i.expires_at, garage.timezone) })}</span>
                {canManage && <Button variant="outline" size="sm" onClick={() => resend(i)} data-testid={`resend-invite-${i.id}`}><RotateCw className="mr-1 h-3.5 w-3.5" />{t("p7.access.resend")}</Button>}
                {canManage && <Button variant="outline" size="sm" onClick={() => revoke(i)} data-testid={`revoke-invite-${i.id}`}>{t("employees.revoke")}</Button>}
              </div>
            ))}
          </div>
        )}
      </Card>
      {canManage ? (
        <Card>
          <SectionTitle hint={t("employees.inviteHint")}>{t("employees.invite")}</SectionTitle>
          <InviteForm onDone={() => { reload(); plan.reload(); }} />
          <div className="mt-3"><TempMemberButton endpoint="/garage/employees/temp-password" onDone={() => { reload(); plan.reload(); }} testId="employee-temp-password-button" /></div>
        </Card>
      ) : (
        data.members.length === 0 && <EmptyState icon={Users} title={t("employees.readOnly")} />
      )}
      <ConfirmDialog open={!!deactivating} onOpenChange={(o) => !o && setDeactivating(null)} destructive title={t("p7.access.deactivateTitle", { name: deactivating?.name })} description={t("p7.access.deactivateText")} confirmLabel={t("p7.access.deactivate")} onConfirm={() => setStatus(deactivating, "inactive")} testId="deactivate-employee-dialog" />
      <ConfirmDialog open={!!removing} onOpenChange={(o) => !o && setRemoving(null)} destructive title={t("employees.removeTitle")} description={t("employees.removeText", { name: removing?.name })} confirmLabel={t("common.remove")} onConfirm={remove} testId="remove-employee-dialog" />
    </div>
  );
}
